/**
 * Компонент для отображения прогресса загрузки файлов
 * Показывает процент, скорость, оставшееся время и размер файла
 * Автор: AI Market Hub
 * Оптимизировано: Декабрь 2024
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Форматирует размер файла в читаемый формат
 */
function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  if (!bytes || isNaN(bytes)) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 1 ? 2 : 0)} ${sizes[i]}`;
}

/**
 * Форматирует скорость в читаемый формат
 */
function formatSpeed(bytesPerSec) {
  if (!bytesPerSec || bytesPerSec <= 0 || isNaN(bytesPerSec)) return '—';
  if (bytesPerSec >= 1024 * 1024) {
    return `${(bytesPerSec / 1024 / 1024).toFixed(1)} MB/s`;
  }
  if (bytesPerSec >= 1024) {
    return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
  }
  return `${bytesPerSec.toFixed(0)} B/s`;
}

/**
 * Форматирует время в читаемый формат
 */
function formatTime(seconds) {
  if (!seconds || seconds <= 0 || isNaN(seconds) || !isFinite(seconds)) return '—';
  if (seconds < 60) return `~${Math.round(seconds)} сек`;
  if (seconds < 3600) return `~${Math.round(seconds / 60)} мин`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return `~${hours} ч ${mins} мин`;
}

/**
 * Компонент прогресс-бара для одного файла
 */
export function UploadProgressItem({ 
  fileName, 
  progress, 
  uploadedBytes, 
  totalBytes, 
  speed, 
  onCancel,
  error 
}) {
  // Вычисляем оставшееся время
  const remainingTime = useMemo(() => {
    if (speed > 0 && progress < 100 && totalBytes > uploadedBytes) {
      return (totalBytes - uploadedBytes) / speed;
    }
    return null;
  }, [speed, uploadedBytes, totalBytes, progress]);

  // Определяем статус
  const status = useMemo(() => {
    if (error) return 'error';
    if (progress >= 100) return 'complete';
    if (progress > 0) return 'uploading';
    return 'pending';
  }, [progress, error]);

  // Цвет прогресс-бара
  const progressBarClass = useMemo(() => {
    switch (status) {
      case 'error': return 'bg-red-500';
      case 'complete': return 'bg-green-500';
      default: return 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500';
    }
  }, [status]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white/5 border border-white/10 rounded-xl p-4 mb-3"
    >
      {/* Заголовок с именем файла */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate" title={fileName}>
            📹 {fileName}
          </p>
        </div>
        {onCancel && status === 'uploading' && (
          <button
            onClick={onCancel}
            className="ml-2 text-gray-400 hover:text-red-400 transition-colors p-1"
            title="Отменить загрузку"
          >
            ✕
          </button>
        )}
      </div>

      {/* Прогресс-бар */}
      <div className="w-full bg-gray-800 rounded-full h-2.5 overflow-hidden mb-3">
        <motion.div
          className={`h-full rounded-full ${progressBarClass}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(progress, 100)}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {/* Размер */}
        <div className="flex items-center gap-1 text-gray-400">
          <span className="text-gray-500">📦</span>
          <span>{formatFileSize(uploadedBytes)} / {formatFileSize(totalBytes)}</span>
        </div>
        
        {/* Процент */}
        <div className="flex items-center gap-1 text-gray-400 justify-end">
          {status === 'error' ? (
            <span className="text-red-400">❌ Ошибка</span>
          ) : status === 'complete' ? (
            <span className="text-green-400">✅ Готово</span>
          ) : (
            <span className="text-purple-400 font-medium">{Math.round(progress)}%</span>
          )}
        </div>
        
        {/* Скорость */}
        <div className="flex items-center gap-1 text-gray-400">
          <span className="text-gray-500">⚡</span>
          <span>{formatSpeed(speed)}</span>
        </div>
        
        {/* Оставшееся время */}
        <div className="flex items-center gap-1 text-gray-400 justify-end">
          <span className="text-gray-500">⏱️</span>
          <span>{remainingTime !== null ? formatTime(remainingTime) : '—'}</span>
        </div>
      </div>

      {/* Сообщение об ошибке */}
      {error && (
        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}
    </motion.div>
  );
}

/**
 * Контейнер для нескольких прогресс-баров
 */
export default function UploadProgress({ uploads = [], onCancel }) {
  // Вычисляем общую статистику
  const stats = useMemo(() => {
    if (uploads.length === 0) return null;
    
    const totalBytes = uploads.reduce((sum, u) => sum + (u.totalBytes || 0), 0);
    const uploadedBytes = uploads.reduce((sum, u) => sum + (u.uploadedBytes || 0), 0);
    const avgSpeed = uploads.reduce((sum, u) => sum + (u.speed || 0), 0) / uploads.length;
    const overallProgress = totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;
    const activeCount = uploads.filter(u => u.progress > 0 && u.progress < 100 && !u.error).length;
    const completedCount = uploads.filter(u => u.progress >= 100).length;
    const errorCount = uploads.filter(u => u.error).length;
    
    return {
      totalBytes,
      uploadedBytes,
      avgSpeed,
      overallProgress,
      activeCount,
      completedCount,
      errorCount
    };
  }, [uploads]);

  if (uploads.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-md w-full pointer-events-none">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="bg-[#0a0a0a]/95 border border-white/10 rounded-2xl p-4 shadow-2xl backdrop-blur-xl pointer-events-auto"
        >
          {/* Заголовок */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                📤 Загрузка видео
                {stats && (
                  <span className="text-xs font-normal text-gray-400">
                    ({stats.completedCount}/{uploads.length})
                  </span>
                )}
              </h3>
              {stats && stats.activeCount > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatFileSize(stats.uploadedBytes)} из {formatFileSize(stats.totalBytes)} • {formatSpeed(stats.avgSpeed)}
                </p>
              )}
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-white transition-colors text-sm px-2 py-1 rounded hover:bg-white/10"
              >
                Свернуть
              </button>
            )}
          </div>

          {/* Общий прогресс-бар */}
          {stats && uploads.length > 1 && (
            <div className="w-full bg-gray-800 rounded-full h-1 overflow-hidden mb-3">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-green-500"
                initial={{ width: 0 }}
                animate={{ width: `${stats.overallProgress}%` }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              />
            </div>
          )}

          {/* Список загрузок */}
          <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
            {uploads.map((upload) => (
              <UploadProgressItem
                key={upload.id}
                fileName={upload.fileName}
                progress={upload.progress}
                uploadedBytes={upload.uploadedBytes}
                totalBytes={upload.totalBytes}
                speed={upload.speed}
                onCancel={upload.onCancel}
                error={upload.error}
              />
            ))}
          </div>

          {/* Статус ошибок */}
          {stats && stats.errorCount > 0 && (
            <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-xs text-red-400">
                ⚠️ {stats.errorCount} из {uploads.length} файлов не удалось загрузить
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
